import { apiFetch } from "../api.js";

/**
 * Documents api-module — Phase 10 (2026-05-08).
 *
 * Mirror types of `shared/src/schemas/documents.ts`.
 */

export type DocType =
  | "passport"
  | "birth_certificate"
  | "marriage_certificate"
  | "divorce_decree"
  | "green_card"
  | "social_security_card"
  | "drivers_license"
  | "vehicle_title"
  | "vehicle_registration"
  | "deed"
  | "ho6_share_certificate"
  | "tax_form"
  | "medical_record"
  | "vaccination_record"
  | "school_record"
  | "military_dd214"
  | "hsa_receipt"
  | "other";

export interface VaultDocument {
  household_id: string;
  doc_id: string;
  doc_type: DocType;
  display_name: string;
  expiration_date?: string;
  last_reviewed_at?: string;
  notes?: string;
  s3_object_key?: string;
  s3_uploaded_at?: string;
  original_filename?: string;
  content_type?: string;
  byte_size?: number;
  linked_hsa_receipt_id?: string;
  ocr_completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ListDocumentsResponse {
  items: VaultDocument[];
  count: number;
}

export interface PresignedUploadResponse {
  upload_url: string;
  expires_in_seconds: number;
  s3_object_key: string;
  required_headers: Record<string, string>;
}

export interface PresignedDownloadResponse {
  download_url: string;
  expires_in_seconds: number;
}

export interface CreateDocumentInput {
  doc_type: DocType;
  display_name: string;
  expiration_date?: string | undefined;
  notes?: string | undefined;
  linked_hsa_receipt_id?: string | undefined;
}

export function listDocuments(filters?: {
  doc_type?: DocType;
  expiring_within_days?: number;
}): Promise<ListDocumentsResponse> {
  const params = new URLSearchParams();
  if (filters?.doc_type) params.set("doc_type", filters.doc_type);
  if (filters?.expiring_within_days !== undefined)
    params.set(
      "expiring_within_days",
      String(filters.expiring_within_days),
    );
  const qs = params.toString();
  return apiFetch<ListDocumentsResponse>(
    `/v1/documents${qs ? `?${qs}` : ""}`,
  );
}

export function createDocument(
  input: CreateDocumentInput,
): Promise<VaultDocument> {
  return apiFetch<VaultDocument>("/v1/documents", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateDocument(
  docId: string,
  patch: Record<string, unknown>,
): Promise<VaultDocument> {
  return apiFetch<VaultDocument>(`/v1/documents/${encodeURIComponent(docId)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteDocument(docId: string): Promise<void> {
  return apiFetch<void>(`/v1/documents/${encodeURIComponent(docId)}`, {
    method: "DELETE",
  });
}

export function getDocumentUploadUrl(
  docId: string,
  payload: { filename: string; content_type: string; byte_size?: number },
): Promise<PresignedUploadResponse> {
  return apiFetch<PresignedUploadResponse>(
    `/v1/documents/${encodeURIComponent(docId)}/upload-url`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function getDocumentDownloadUrl(
  docId: string,
): Promise<PresignedDownloadResponse> {
  return apiFetch<PresignedDownloadResponse>(
    `/v1/documents/${encodeURIComponent(docId)}/download-url`,
  );
}
